import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ScenarioCard from '../components/ScenarioCard';
import { getAllScenarios, CATEGORIES } from '../data/scenarios';
import UserMenu from '../components/auth/UserMenu';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import { useAuth } from '../components/auth/AuthContext';

const PIPELINE_STEPS = [
  { icon: '📸', label: '촬영', desc: '사진 촬영/업로드' },
  { icon: '🔍', label: '분석', desc: 'AI 자동 분석' },
  { icon: '💾', label: '기록', desc: '결과 저장' },
  { icon: '🖨️', label: '출력', desc: 'PDF/공유' },
];

const CATEGORY_TABS = [
  { key: '', label: '전체' },
  ...Object.entries(CATEGORIES).map(([key, cat]) => ({
    key,
    label: `${cat.icon} ${cat.label}`,
  })),
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const scenarios = getAllScenarios();
  const [selectedCategory, setSelectedCategory] = useState('');

  const filteredScenarios = selectedCategory
    ? scenarios.filter(s => s.category === selectedCategory)
    : scenarios;

  const grouped = {};
  for (const s of filteredScenarios) {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  }

  const handleScenarioClick = (scenarioId) => {
    if (!user) return;
    navigate(`/snap/${scenarioId}`);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--gradient-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: 'var(--font-size-lg)',
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      color: 'white',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* User Menu */}
      {user && (
        <div style={{
          position: 'absolute',
          top: 'var(--space-lg)',
          right: 'var(--space-lg)',
          zIndex: 100,
        }}>
          <UserMenu />
        </div>
      )}

      {/* Hero */}
      <div style={{
        textAlign: 'center',
        padding: user ? 'var(--space-2xl) var(--space-lg) var(--space-xl)' : 'var(--space-3xl) var(--space-lg) var(--space-2xl)',
        width: '100%',
        maxWidth: 'var(--max-width)',
      }}>
        <h1
          className="gradient-text"
          style={{
            fontSize: user ? 'var(--font-size-3xl)' : 'clamp(2.5rem, 8vw, 4rem)',
            marginBottom: 'var(--space-sm)',
            fontWeight: 800,
            opacity: 0,
            animation: 'fadeInUp 0.6s ease forwards',
            WebkitTextFillColor: 'transparent',
            background: 'linear-gradient(135deg, #a8c0ff 0%, #c4b5fd 50%, #f0abfc 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
          }}
        >
          SnapLog
        </h1>
        <p style={{
          fontSize: user ? 'var(--font-size-lg)' : 'var(--font-size-xl)',
          opacity: 0,
          animation: 'fadeInUp 0.6s ease 0.1s forwards',
          fontWeight: user ? 400 : 500,
        }}>
          찍고, 분석하고, 기록하다
        </p>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          opacity: 0,
          animation: 'fadeInUp 0.6s ease 0.2s forwards',
          color: 'rgba(255,255,255,0.6)',
          marginTop: 'var(--space-sm)',
          maxWidth: '400px',
          margin: 'var(--space-sm) auto 0',
          lineHeight: 1.6,
        }}>
          AI가 사진을 분석하여 학습 오답노트와 설비점검 보고서를 자동 생성합니다
        </p>
      </div>

      {/* Pipeline Visualization */}
      <div style={{
        width: '100%',
        maxWidth: 'var(--max-width)',
        padding: '0 var(--space-lg)',
        marginBottom: 'var(--space-xl)',
        opacity: 0,
        animation: 'fadeInUp 0.6s ease 0.25s forwards',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-lg) var(--space-sm)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}>
          {PIPELINE_STEPS.map((step, i) => (
            <React.Fragment key={step.label}>
              <div style={{
                textAlign: 'center',
                flex: 1,
                minWidth: 0,
                opacity: 0,
                animation: 'fadeInUp 0.4s ease forwards',
                animationDelay: `${0.4 + i * 0.1}s`,
              }}>
                <div style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  marginBottom: '0.25rem',
                  animation: `pulse 3s ease-in-out ${i * 0.5}s infinite`,
                }}>
                  {step.icon}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{step.label}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{step.desc}</div>
              </div>
              {i < PIPELINE_STEPS.length - 1 && (
                <div style={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: 'var(--font-size-lg)',
                  flexShrink: 0,
                  opacity: 0,
                  animation: 'fadeInUp 0.4s ease forwards',
                  animationDelay: `${0.45 + i * 0.1}s`,
                }}>
                  →
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 비로그인: 서비스 소개 + 로그인 */}
      {!user && (
        <div style={{
          width: '100%',
          maxWidth: 'var(--max-width)',
          padding: '0 var(--space-lg)',
          marginBottom: 'var(--space-xl)',
          opacity: 0,
          animation: 'fadeInUp 0.6s ease 0.3s forwards',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: 'var(--space-xl)',
            flexWrap: 'wrap',
            maxWidth: '600px',
            margin: '0 auto var(--space-2xl)',
          }}>
            <FeatureItem image="/images/feature-capture.png" title="사진 촬영" desc="문제나 점검대상을 촬영" />
            <FeatureItem image="/images/feature-ai.png" title="AI 분석" desc="자동으로 내용 분석 및 정리" />
            <FeatureItem image="/images/feature-storage.png" title="기록 저장" desc="언제든 복습하고 확인" />
          </div>
          <div style={{ maxWidth: '320px', margin: '0 auto' }}>
            <GoogleLoginButton />
            <p style={{
              marginTop: 'var(--space-md)',
              fontSize: 'var(--font-size-xs)',
              color: 'rgba(255,255,255,0.5)',
              textAlign: 'center',
            }}>
              로그인하면 기록이 저장되어 어디서든 확인할 수 있습니다
            </p>
          </div>
        </div>
      )}

      {/* Scenario card grid */}
      <div style={{ width: '100%', maxWidth: 'var(--max-width)', padding: '0 var(--space-lg) var(--space-xl)' }}>
        {/* 비로그인 안내 */}
        {!user && (
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-md) var(--space-lg)',
            marginBottom: 'var(--space-lg)',
            textAlign: 'center',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            opacity: 0,
            animation: 'fadeInUp 0.5s ease 0.4s forwards',
          }}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'rgba(255,255,255,0.8)' }}>
              로그인 후 아래 시나리오를 사용할 수 있습니다
            </span>
          </div>
        )}

        {/* Category Filter Tabs */}
        <div
          className="hide-scrollbar"
          style={{
            display: 'flex',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-lg)',
            overflowX: 'auto',
            paddingBottom: '4px',
            opacity: 0,
            animation: 'fadeInUp 0.5s ease 0.35s forwards',
          }}
        >
          {CATEGORY_TABS.map(tab => {
            const isActive = selectedCategory === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setSelectedCategory(tab.key)}
                style={{
                  padding: '0.5rem 1.2rem',
                  border: 'none',
                  borderRadius: 'var(--radius-full)',
                  background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: isActive ? 700 : 400,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  whiteSpace: 'nowrap',
                  transition: 'all var(--transition-fast)',
                  boxShadow: isActive ? '0 0 0 2px rgba(255,255,255,0.4)' : 'none',
                  flexShrink: 0,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {Object.entries(grouped).map(([catKey, catScenarios], groupIdx) => {
          const cat = CATEGORIES[catKey];
          return (
            <div key={catKey + '-' + selectedCategory} style={{ marginBottom: 'var(--space-xl)' }}>
              <h2 style={{
                fontSize: '1.2rem',
                marginBottom: 'var(--space-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease forwards',
                animationDelay: `${0.3 + groupIdx * 0.1}s`,
              }}>
                {cat.icon} {cat.label}
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 'var(--space-md)',
              }}>
                {catScenarios.map((scenario, idx) => (
                    <div
                      key={scenario.id}
                      style={{
                        opacity: 0,
                        animation: 'fadeInUp 0.5s ease forwards',
                        animationDelay: `${0.3 + groupIdx * 0.1 + idx * 0.08}s`,
                        position: 'relative',
                      }}
                    >
                      <ScenarioCard
                        scenario={scenario}
                        onClick={() => handleScenarioClick(scenario.id)}
                        disabled={!user}
                      />
                      {!user && (
                        <div
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'transparent',
                            borderRadius: 'var(--radius-xl)',
                            cursor: 'pointer',
                          }}
                        />
                      )}
                    </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeatureItem({ image, title, desc }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: 'var(--space-md)',
      minWidth: '140px',
      maxWidth: '180px',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        margin: '0 auto var(--space-sm)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}>
        <img
          src={image}
          alt={title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
      <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: 'var(--font-size-sm)' }}>{title}</div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{desc}</div>
    </div>
  );
}
